<template>
  <ViewContainer title="">
    <v-container fill-height class="mb-4 px-4 pb-6">
      <v-row class="my-8">
        <v-col offset="2" cols="8">
          <v-card class="pa-0 ma-0 mt-2 pa-8">
            <v-card-title
              class="ma-0 pa-0 h6 blue-grey--text text--darken-2 mb-1"
            >
              {{ $t('boards.edit_board') }}
            </v-card-title>
            <v-card-text class="ma-0 pa-0 mt-8 pb-4">
              <v-form ref="boardForm" @submit.prevent>
                <v-text-field
                  outlined
                  dense
                  counter="60"
                  :maxLength="60"
                  :label="`${$t('common.name')}*`"
                  :rules="[
                    name =>
                      !!name || $t('validation.form.this_field_is_required'),
                  ]"
                  v-model="board.name"
                />
                <v-textarea
                  v-model="board.description"
                  outlined
                  dense
                  auto-grow
                  no-resize
                  rows="3"
                  counter="170"
                  :maxLength="170"
                  :label="`${$t('boards.description_optional')}`"
                />
                <v-divider />
                <p class="my-6 subtitle-2 blue-grey--text text--darken-3">
                  {{ $t('boards.column_other') }}:
                </p>
                <v-divider />
                <Draggable
                  ghost-class="ghost-item"
                  handle=".column-drag-control"
                  :list="columns"
                  @change="updatePositions(true)"
                >
                  <div
                    v-for="(column, index) in columns"
                    class="board-column-container"
                    :key="`column-${index}-pos${column.position}`"
                  >
                    <div
                      class="pa-4 body-1 blue-grey--text text--darken-3 d-flex"
                    >
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            v-on="on"
                            color="blue-grey"
                            size="24"
                            class="column-drag-control mr-4"
                            >drag_indicator</v-icon
                          >
                        </template>
                        {{ $t('common.drag_and_drop') }}
                      </v-tooltip>
                      <p class="ma-0 flex-grow-1">
                        {{ column.name }}
                      </p>
                      <v-icon
                        class="mr-3 cursor-pointer"
                        size="24"
                        color="blue-grey darken-1"
                        @click="openEditColumnModal(column)"
                        >mdi-pencil-outline</v-icon
                      >
                      <v-icon
                        class=" cursor-pointer"
                        size="24"
                        color="blue-grey darken-1"
                        @click="openDeleteColumnConfirmation(column)"
                        >mdi-delete-outline</v-icon
                      >
                    </div>
                    <v-divider />
                  </div>
                </Draggable>
              </v-form>
            </v-card-text>
            <v-card-actions class="ma-0 pa-0 mt-4 justify-end">
              <v-btn text rounded dense color="primary" @click="handleBack">{{
                $t('common.button.cancel')
              }}</v-btn>
              <v-btn
                rounded
                dense
                color="blue"
                class="white--text px-4 ms-3"
                elevation="0"
                :loading="loading"
                :disabled="loading"
                @click="handleSave"
                >{{ $t('common.button.save_changes') }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <BoardColumnModal
      :show="openColumnModal"
      :title="$t('boards.edit_column')"
      :value="currentColumn"
      @close="openColumnModal = false"
      @save="handleSaveColumn"
    />
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :title="$t('boards.delete_column_title')"
      :text="$t('boards.delete_column_subtitle', { name: currentColumn.name })"
      @cancel="handleCancelDeleteColumn"
      @delete="handleDeleteColumn(currentColumn)"
    />
  </ViewContainer>
</template>

<script>
import Draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import BoardColumnModal from '../BoardView/Column/ColumnModal.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

export default {
  name: 'EditBoardView',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    Draggable,
    BoardColumnModal,
    DeleteConfirmationModal,
  },

  data() {
    return {
      boardId: this.$route.params.boardId,
      board: {
        name: '',
        description: '',
      },
      initialBoard: {},
      columns: [],
      initialColumns: [],
      columnIdsToDelete: [],
      loading: false,
      openColumnModal: false,
      currentColumn: this.$constants.EMPTY_COLUMN,
      formIsValid: true,
      showDeleteModal: false,
      prevRoute: null,
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  async mounted() {
    await this.getBoard()

    await this.getAllColumns()
  },

  computed: {
    ...mapGetters(['organizationId']),
  },

  methods: {
    handleBack() {
      if (this.prevRoute?.name) {
        this.$router.back()
      } else {
        this.navigateTo({ name: 'board', params: { boardId: this.boardId } })
      }
    },

    async getBoard() {
      try {
        this.loading = true
        const response = await api.board.getById({
          organizationId: this.organizationId,
          boardId: this.boardId,
        })

        this.board = response.data
        this.initialBoard = { ...response.data }

        this.totalColumns = response.data.columnsCount
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getAllColumns() {
      try {
        this.loading = true

        if (this.totalColumns === 0) return

        const limit = 20
        const totalPages = Math.ceil(this.totalColumns / limit)

        for (let page = 1; page <= totalPages; page++) {
          const { results } = await this.getBoardColumns({
            skip: (page - 1) * limit,
            limit,
          })

          if (results.length) {
            this.columns.push(...this.parseColumns(results))
          }
        }
        this.initialColumns = [...this.columns]
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getBoardColumns(options) {
      const response = await api.board.getColumns({
        organizationId: this.organizationId,
        boardId: this.boardId,
        limit: options.limit,
        skip: options.skip,
        sort: { by: 'position', order: 'asc' },
      })

      return response.data
    },

    parseColumns(columns) {
      return columns.map(column => {
        return {
          ...column,
          filter: _.omitBy(
            column?.filter,
            (value, key) =>
              (_.isArray(value) && _.isEmpty(value)) ||
              _.isNull(value) ||
              key === '_id'
          ),
        }
      })
    },

    async handleSave() {
      this.formIsValid = this.$refs.boardForm.validate()
      if (this.formIsValid) {
        try {
          this.loading = true
          await this.updateBoard()
          await this.updateColumns()
          await this.deleteColumns()
          this.initialBoard = { ...this.board }
          this.initialColumns = [...this.columns]
          this.showSuccess(this.$t('boards.board_updated_successfully'))
          this.navigateTo({ name: 'board', params: { boardId: this.boardId } })
          this.trackBoardsActions('boards.SaveChangesEditFlow', {
            ...this.board,
            nameLength: this.board.name.length,
            descriptionLength: this.board.description.length,
            columns: this.columns,
          })
        } catch (err) {
          this.handleRequestError(err)
        } finally {
          this.loading = false
        }
      }
    },

    async updateBoard() {
      if (_.isEqual(this.board, this.initialBoard)) return
      await api.board.update({
        organizationId: this.organizationId,
        boardId: this.boardId,
        data: this.board,
      })
    },

    async updateColumns() {
      if (_.isEqual(this.columns, this.initialColumns)) return
      await api.board.updateColumnBulk({
        organizationId: this.organizationId,
        boardId: this.boardId,
        columns: this.columns.map(column => ({
          ...column,
          userFilter: column?.userFilter?.id,
        })),
      })
    },

    async deleteColumns() {
      if (!this.columnIdsToDelete.length) return
      await api.board.deleteColumnBulk({
        organizationId: this.organizationId,
        boardId: this.boardId,
        columnIds: this.columnIdsToDelete,
      })
    },

    openEditColumnModal(column) {
      this.currentColumn = column
      this.openColumnModal = true
    },

    handleSaveColumn(updatedColumn) {
      this.columns = this.columns.map(column => {
        if (column.id === updatedColumn.id) {
          return {
            ...column,
            ...updatedColumn,
          }
        }
        return column
      })
      this.trackBoardsActions('boards.ConfirmAction', {
        action: 'edit column',
        column: updatedColumn,
      })
    },

    openDeleteColumnConfirmation(column) {
      this.currentColumn = column
      this.showDeleteModal = true
      this.trackBoardsActions('boards.DeleteColumnEditFlow')
    },

    handleDeleteColumn(deletedColumn) {
      this.showDeleteModal = false
      this.columnIdsToDelete.push(deletedColumn.id)
      this.columns = this.columns.filter(
        column => column.id !== deletedColumn.id
      )
      this.updatePositions()
      this.trackBoardsActions('boards.ConfirmAction', {
        action: 'delete column',
      })
    },

    updatePositions(trackEvent = false) {
      this.columns = this.columns.map((column, index) => {
        return {
          ...column,
          position: index,
        }
      })
      if (trackEvent) {
        this.trackBoardsActions('boards.DragAndDropColumn', {
          section: 'edit board',
        })
      }
    },

    handleCancelDeleteColumn() {
      this.showDeleteModal = false
      this.trackBoardsActions('boards.CancelAction', {
        action: 'delete column',
      })
    },
  },

  beforeRouteLeave(to, from, next) {
    const noBoardChanges = _.isEqual(this.board, this.initialBoard)
    const noColumnsChanges = _.isEqual(this.columns, this.initialColumns)
    if (noBoardChanges && noColumnsChanges) return next()
    if (window.confirm(this.$t('boards.unsaved_board'))) {
      next()
    } else {
      next(false)
    }
  },
}
</script>

<style scoped lang="scss">
.ghost-item {
  opacity: 0.5;
}
.board-column-container {
  .column-drag-control {
    cursor: move;
    visibility: hidden;
  }

  &:hover {
    .column-drag-control {
      visibility: visible;
    }
  }
}
</style>
